import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Wallet, Plus, Loader2, Lock, ShieldAlert, Upload, ClipboardCheck, BookOpen,
  MoreVertical, History, Copy, Pencil, Trash2, Power, RotateCcw, Ban, Sparkles,
  ClipboardList, AlertTriangle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });
const todayStr = () => new Date().toISOString().slice(0, 10);

const GROUP_TABS = [
  { key: "all", label: "All", group: null },
  { key: "today", label: "Today", group: null, today: true },
  { key: "SALES", label: "Cash Sales", group: "SALES" },
  { key: "SERVICE", label: "Service Cash", group: "SERVICE" },
  { key: "PETTY", label: "Petty Cash", group: "PETTY" },
  { key: "BUSINESS", label: "Business Cash", group: "BUSINESS" },
  { key: "PERSONAL", label: "Personal Cash", group: "PERSONAL", personal: true },
];
const TXN_TYPES = [
  "CASH_SALE", "SERVICE_CASH_RECEIPT", "PETTY_CASH_EXPENSE", "BUSINESS_CASH_RECEIPT",
  "BUSINESS_CASH_PAYMENT", "PERSONAL_CASH_RECEIPT", "PERSONAL_CASH_PAYMENT",
  "CUSTOMER_PAYMENT", "VENDOR_PAYMENT", "CASH_ADVANCE", "CASH_REFUND", "CASH_TRANSFER",
  "CASH_TO_BANK_DEPOSIT", "BANK_TO_CASH_WITHDRAWAL", "OPENING_BALANCE", "ADJUSTMENT", "OTHER",
];

const CASH_CATEGORIES = [
  "Sales", "Service Income", "Purchase", "Salary & Wages", "Rent", "Electricity",
  "Water", "Internet & Telephone", "Fuel & Travel", "Office Supplies", "Stationery",
  "Repairs & Maintenance", "Courier & Postage", "Bank Charges", "Taxes & Duties",
  "Professional Fees", "Marketing", "Refreshments", "Cleaning", "Insurance",
  "Loan / Advance", "Reimbursement", "Miscellaneous",
];

export default function CashControl() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [access, setAccess] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [tab, setTab] = useState("all");
  const [acctId, setAcctId] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [addAcctOpen, setAddAcctOpen] = useState(false);
  const [editAcct, setEditAcct] = useState(null);
  const [acctHistory, setAcctHistory] = useState(null);
  const [delAcct, setDelAcct] = useState(null);

  const loadAccounts = useCallback(async () => {
    try { const { data } = await api.get("/cash-control/accounts"); setAccounts(data.items || []); if (!acctId && data.items?.[0]) setAcctId(data.items[0].id); }
    catch { /* */ }
  }, [acctId]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/cash-control/access"); setAccess(data); if (data.can_access) loadAccounts(); }
      catch { setAccess({ can_access: false }); }
    })();
  }, []); // eslint-disable-line

  // Universal Create deep-links: open the right form straight away.
  useEffect(() => {
    if (!access?.can_access) return;
    const action = new URLSearchParams(window.location.search).get("action");
    if (action === "add-txn" || action === "payment") setAddOpen(true);
    else if (action === "add-account") setAddAcctOpen(true);
    if (action) window.history.replaceState({}, "", window.location.pathname);
  }, [access]);

  if (access === null) return <div className="p-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!access.can_access) return (
    <div className="p-10 text-center" data-testid="cash-control-denied">
      <ShieldAlert className="h-10 w-10 text-rose-500 mx-auto mb-2" />
      <div className="font-semibold">Cash Control is restricted</div>
      <p className="text-sm text-slate-500">Only Finance administrators can access this workspace.</p>
    </div>
  );

  const tabs = GROUP_TABS.filter((t) => !t.personal || access.can_personal);

  const deactivateAcct = async (a) => {
    try { const { data } = await api.post(`/cash-control/accounts/${a.id}/deactivate`);
      toast.success(`Account ${data.status === "INACTIVE" ? "deactivated" : "reactivated"}`); loadAccounts(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const tryDeleteAcct = async (a) => {
    if (a.txn_count > 0) { setDelAcct(a); return; }
    if (!window.confirm(`Delete cash account "${a.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/cash-control/accounts/${a.id}`); toast.success("Account deleted");
      if (acctId === a.id) setAcctId(""); loadAccounts(); }
    catch (e) { const d = e.response?.data?.detail; if (typeof d === "object") setDelAcct(a); else toast.error(d || "Delete failed"); }
  };
  const forceDeleteAcct = async (a) => {
    try {
      const { data } = await api.delete(`/cash-control/accounts/${a.id}?force=true`);
      toast.success(`Account permanently deleted${data.purged_txns ? ` · ${data.purged_txns} transaction(s) purged` : ""}`);
      if (acctId === a.id) setAcctId(""); setDelAcct(null); loadAccounts();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "object" ? d.message : d) || "Permanent delete failed");
    }
  };

  return (
    <div className="p-6 max-w-[1500px] mx-auto" data-testid="cash-control-page">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2">
            <Wallet className="h-6 w-6 text-emerald-700" /> Cash Control
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200 flex items-center gap-1"><Lock className="h-3 w-3" /> Restricted</span>
          </h1>
          <p className="text-sm text-slate-500">ONE Finance Vault · cash registers, daily closing and auditor-grade history.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setAddAcctOpen(true)} data-testid="cash-add-account-btn"><Plus className="h-4 w-4 mr-1" /> Cash account</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => setAddOpen(true)} data-testid="cash-add-txn-btn"><Plus className="h-4 w-4 mr-1" /> Add cash transaction</Button>
        </div>
      </div>

      <AccountTiles accounts={accounts} acctId={acctId} onPick={setAcctId}
        onEdit={setEditAcct} onDelete={tryDeleteAcct} onDeactivate={deactivateAcct}
        onReconcile={(a) => { setAcctId(a.id); setTab("__closing"); }}
        onHistory={setAcctHistory} />

      <div className="flex gap-1 border-b border-slate-200 my-4 overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`cash-tab-${t.key}`}
            className={`px-3 py-2 text-[13px] font-medium whitespace-nowrap border-b-2 -mb-px ${tab === t.key ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t.label}
          </button>
        ))}
        <button onClick={() => setTab("__closing")} data-testid="cash-tab-closing" className={`px-3 py-2 text-[13px] font-medium border-b-2 -mb-px ${tab === "__closing" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}><ClipboardCheck className="h-3.5 w-3.5 inline mr-1" />Daily Closing</button>
        <button onClick={() => setTab("__import")} data-testid="cash-tab-import" className={`px-3 py-2 text-[13px] font-medium border-b-2 -mb-px ${tab === "__import" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}><Upload className="h-3.5 w-3.5 inline mr-1" />Import</button>
        <button onClick={() => setTab("__auditor")} data-testid="cash-tab-auditor" className={`px-3 py-2 text-[13px] font-medium border-b-2 -mb-px ${tab === "__auditor" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}><BookOpen className="h-3.5 w-3.5 inline mr-1" />Auditor</button>
      </div>

      {!tab.startsWith("__") && <Register key={refreshKey} tab={tabs.find((t) => t.key === tab) || tabs[0]} acctId={acctId} accounts={accounts} canPersonal={access.can_personal} onBalancesChanged={loadAccounts} />}
      {tab === "__closing" && <DailyClosing acctId={acctId} accounts={accounts} />}
      {tab === "__import" && <ImportPanel acctId={acctId} accounts={accounts} />}
      {tab === "__auditor" && <AuditorView acctId={acctId} accounts={accounts} />}

      {addOpen && <AddTxn accounts={accounts} defaultAcct={acctId} canPersonal={access.can_personal}
        onClose={() => setAddOpen(false)} onSaved={() => { loadAccounts(); setRefreshKey((k) => k + 1); }} />}
      {addAcctOpen && <AddAccount canPersonal={access.can_personal} onClose={() => setAddAcctOpen(false)}
        onSaved={() => { setAddAcctOpen(false); loadAccounts(); }} />}
      {editAcct && <EditAccount account={editAcct} onClose={() => setEditAcct(null)}
        onSaved={() => { setEditAcct(null); loadAccounts(); }} />}
      {acctHistory && <AccountHistoryDialog account={acctHistory} onClose={() => setAcctHistory(null)} />}
      {delAcct && <AccountDeleteBlockedDialog account={delAcct}
        canForce={isSuper}
        onClose={() => setDelAcct(null)}
        onForceDelete={() => forceDeleteAcct(delAcct)}
        onDeactivate={() => { deactivateAcct(delAcct); setDelAcct(null); }}
        onView={() => { setAcctId(delAcct.id); setDelAcct(null); }} />}
    </div>
  );
}

function AccountTiles({ accounts, acctId, onPick, onEdit, onDelete, onDeactivate, onReconcile, onHistory }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="cash-account-tiles">
      {accounts.map((a) => (
        <div key={a.id}
          className={`relative text-left rounded-lg border p-3 cursor-pointer ${acctId === a.id ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
          onClick={() => onPick(a.id)} data-testid={`cash-acct-${a.id}`}>
          <div className="flex items-start justify-between">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">{a.type}</div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <button className="text-slate-400 hover:text-slate-700 -mt-1 -mr-1 p-1" data-testid={`cash-acct-menu-${a.id}`}>
                  <MoreVertical className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem onClick={() => onEdit(a)} data-testid={`cash-acct-edit-${a.id}`}><Pencil className="h-3.5 w-3.5 mr-2" /> Edit account</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPick(a.id)} data-testid={`cash-acct-view-${a.id}`}><ClipboardList className="h-3.5 w-3.5 mr-2" /> View transactions</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onReconcile(a)} data-testid={`cash-acct-reconcile-${a.id}`}><ClipboardCheck className="h-3.5 w-3.5 mr-2" /> Reconcile</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onHistory(a)} data-testid={`cash-acct-history-${a.id}`}><History className="h-3.5 w-3.5 mr-2" /> View history</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onDeactivate(a)} data-testid={`cash-acct-deactivate-${a.id}`}>
                  <Power className="h-3.5 w-3.5 mr-2" /> {a.status === "INACTIVE" ? "Reactivate" : "Deactivate"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDelete(a)} className="text-rose-600 focus:text-rose-700" data-testid={`cash-acct-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5 mr-2" /> Delete account</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="text-[13px] font-medium truncate flex items-center gap-1">
            {a.name}
            {a.status === "INACTIVE" && <span className="text-[8px] px-1 rounded bg-slate-200 text-slate-500">INACTIVE</span>}
          </div>
          <div className="text-lg font-semibold text-slate-800">{money(a.balance)}</div>
          {a.possible_duplicate_of && (
            <div className="mt-1 text-[9px] text-amber-700 flex items-center gap-1" data-testid={`cash-acct-dup-${a.id}`}>
              <AlertTriangle className="h-3 w-3" /> Possible duplicate
            </div>
          )}
        </div>
      ))}
      {!accounts.length && <div className="text-sm text-slate-400 col-span-4 py-4">No cash accounts yet — add one to begin.</div>}
    </div>
  );
}

function Register({ tab, acctId, accounts = [], canPersonal, onBalancesChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (acctId) params.account_id = acctId;
      if (tab.group) params.group = tab.group;
      if (tab.today) params.today = true;
      if (q) params.q = q;
      const { data } = await api.get("/cash-control/txns", { params });
      setRows(data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  }, [tab, acctId, q]);
  useEffect(() => { load(); }, [load]);
  const reloadAll = () => { load(); onBalancesChanged?.(); };

  const [confirm, setConfirm] = useState(null); // {t, kind}
  const [correct, setCorrect] = useState(null);
  const [history, setHistory] = useState(null);
  const [dupe, setDupe] = useState(null);
  const [postedDelete, setPostedDelete] = useState(null);
  const runConfirm = async (reason) => {
    const { t, kind } = confirm;
    try {
      if (kind === "delete") await api.delete(`/cash-control/txns/${t.id}?reason=${encodeURIComponent(reason)}`);
      if (kind === "reverse") await api.post(`/cash-control/txns/${t.id}/reverse`, { reason });
      if (kind === "void") await api.post(`/cash-control/txns/${t.id}/void`, { reason });
      toast.success(kind === "delete" ? "Deleted" : kind === "reverse" ? "Reversed" : "Voided");
      setConfirm(null); reloadAll();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "object" ? (d.message || d.recommended_action) : d) || "Action failed");
    }
  };

  return (
    <div data-testid="cash-register">
      <Input placeholder="Search party / particulars / reference / voucher…" value={q}
        onChange={(e) => setQ(e.target.value)} className="max-w-sm mb-3" data-testid="cash-register-search" />
      {loading ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
        <div className="border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[9px] tracking-wider">
              <tr>{["Date", "Type", "Particulars", "Paid To / From", "Reference", "Voucher", "Receipt", "Payment", "Balance", "Status", "Actions"].map((h) => <th key={h} className={`px-2 py-2 text-left whitespace-nowrap ${h === "Actions" ? "min-w-[190px]" : ""}`}>{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((t, i) => {
                const isDraft = t.status === "DRAFT";
                const dead = ["VOID", "REVERSED"].includes(t.status);
                return (
                <tr key={t.id} className={`hover:bg-slate-50 ${t.status === "VOID" ? "opacity-40 line-through" : ""}`} data-testid={`cash-row-${i}`}>
                  <td className="px-2 py-1.5 whitespace-nowrap">{t.date}</td>
                  <td className="px-2 py-1.5 text-[10px]">{t.type}</td>
                  <td className="px-2 py-1.5 max-w-[240px] truncate">{t.particulars || "—"}</td>
                  <td className="px-2 py-1.5 max-w-[180px] truncate" data-testid={`cash-party-${i}`}>{t.party || "—"}</td>
                  <td className="px-2 py-1.5 text-[10px]">{t.reference || "—"}</td>
                  <td className="px-2 py-1.5 font-mono text-[10px]">{t.number}</td>
                  <td className="px-2 py-1.5 text-right text-emerald-700">{t.direction === "IN" ? money(t.amount) : ""}</td>
                  <td className="px-2 py-1.5 text-right text-rose-700">{t.direction === "OUT" ? money(t.amount) : ""}</td>
                  <td className="px-2 py-1.5 text-right font-medium">{t.balance != null ? money(t.balance) : "—"}</td>
                  <td className="px-2 py-1.5"><span className="text-[9px] px-1 rounded bg-slate-100">{t.status}</span></td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <div className="flex items-center gap-2.5">
                      {!dead && <button className="text-[11px] text-slate-700 hover:text-slate-900 font-medium" onClick={() => setEdit(t)} data-testid={`cash-edit-${i}`}>Edit</button>}
                      {!dead && !isDraft && <button className="text-[11px] text-sky-700 hover:text-sky-900 font-medium" onClick={() => setCorrect(t)} data-testid={`cash-modify-${i}`}>Modify</button>}
                      {!dead && <button className="text-[11px] text-rose-600 hover:text-rose-700 font-medium"
                        onClick={() => isDraft ? setConfirm({ t, kind: "delete" }) : setPostedDelete(t)}
                        data-testid={`cash-del-${i}`}>Delete</button>}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="text-slate-400 hover:text-slate-700 p-0.5" data-testid={`cash-more-${i}`}><MoreVertical className="h-3.5 w-3.5" /></button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setHistory(t)} data-testid={`cash-history-${i}`}><History className="h-3.5 w-3.5 mr-2" /> View history</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDupe(t)} data-testid={`cash-duplicate-${i}`}><Copy className="h-3.5 w-3.5 mr-2" /> Duplicate</DropdownMenuItem>
                          {!isDraft && !dead && <DropdownMenuSeparator />}
                          {!isDraft && !dead && <DropdownMenuItem onClick={() => setConfirm({ t, kind: "reverse" })} data-testid={`cash-reverse-${i}`}><RotateCcw className="h-3.5 w-3.5 mr-2" /> Reverse</DropdownMenuItem>}
                          {!isDraft && !dead && <DropdownMenuItem onClick={() => setConfirm({ t, kind: "void" })} className="text-rose-600 focus:text-rose-700" data-testid={`cash-void-${i}`}><Ban className="h-3.5 w-3.5 mr-2" /> Void</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ); })}
              {!rows.length && <tr><td colSpan={11} className="px-2 py-8 text-center text-slate-400">No transactions.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {edit && <AddTxn accounts={accounts} defaultAcct={acctId} canPersonal={canPersonal}
        editing={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reloadAll(); }} />}
      {dupe && <AddTxn accounts={accounts} defaultAcct={acctId} canPersonal={canPersonal}
        duplicateOf={dupe} onClose={() => setDupe(null)} onSaved={() => { setDupe(null); reloadAll(); }} />}
      {confirm && <ConfirmReason kind={confirm.kind} txn={confirm.t}
        onClose={() => setConfirm(null)} onConfirm={runConfirm} />}
      {postedDelete && <PostedDeleteDialog txn={postedDelete}
        onClose={() => setPostedDelete(null)}
        onReverse={() => { setConfirm({ t: postedDelete, kind: "reverse" }); setPostedDelete(null); }}
        onVoid={() => { setConfirm({ t: postedDelete, kind: "void" }); setPostedDelete(null); }} />}
      {correct && <CorrectDialog txn={correct} onClose={() => setCorrect(null)}
        onDone={() => { setCorrect(null); reloadAll(); }} />}
      {history && <TxnHistoryDialog txn={history} onClose={() => setHistory(null)} />}
    </div>
  );
}

const CONFIRM_META = {
  delete: { title: "Delete cash entry", verb: "Delete", tone: "bg-rose-600 hover:bg-rose-700",
            note: "This permanently removes the draft entry." },
  reverse: { title: "Reverse cash entry", verb: "Reverse", tone: "bg-amber-600 hover:bg-amber-700",
             note: "A matching reversing entry will be posted; the original is kept and marked Reversed." },
  void: { title: "Void cash entry", verb: "Void", tone: "bg-rose-600 hover:bg-rose-700",
          note: "The entry is marked Void and removed from balances. History is preserved." },
};

function ConfirmReason({ kind, txn, onClose, onConfirm }) {
  const m = CONFIRM_META[kind];
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true); await onConfirm(reason.trim()); setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) setTimeout(onClose, 150); }}>
      <DialogContent data-testid="cash-confirm-dialog">
        <DialogHeader><DialogTitle>{m.title}</DialogTitle></DialogHeader>
        <div className="space-y-2 text-[13px]">
          <div className="text-slate-600">{txn.number} · {txn.particulars || txn.party || ""} · {money(txn.amount)}</div>
          <div className="text-[12px] text-slate-500">{m.note}</div>
          <Label>Reason</Label>
          <Input autoFocus value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Why are you doing this?" data-testid="cash-confirm-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className={`text-white ${m.tone}`} onClick={go} disabled={busy} data-testid="cash-confirm-go">
            {busy ? "Working…" : m.verb}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const CORRECT_FIELDS = [
  ["particulars", "Particulars"], ["party", "Party"], ["reference", "Reference"],
  ["category", "Category"], ["date", "Date"], ["amount", "Amount"],
];

function CorrectDialog({ txn, onClose, onDone }) {
  const [field, setField] = useState("particulars");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (value === "" || !reason.trim()) { toast.error("New value and reason are required"); return; }
    setBusy(true);
    try {
      await api.post(`/cash-control/txns/${txn.id}/correct`,
        { field, new_value: field === "amount" ? Number(value) : value, reason: reason.trim() });
      toast.success("Correction recorded"); onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "object" ? (d.message || d.recommended_action) : d) || "Correction failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) setTimeout(onClose, 150); }}>
      <DialogContent data-testid="cash-correct-dialog">
        <DialogHeader><DialogTitle>Modify cash entry {txn.number}</DialogTitle></DialogHeader>
        <div className="space-y-2 text-[13px]">
          <div className="text-[12px] text-slate-500">A controlled modification with a mandatory reason. The original value is preserved in history and the running balance recalculates automatically.</div>
          <Label>Field to modify</Label>
          <select value={field} onChange={(e) => { setField(e.target.value); setValue(""); }}
            className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white" data-testid="cash-correct-field">
            {CORRECT_FIELDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <div className="text-[11px] text-slate-400">Current: {String(txn[field] ?? "—")}</div>
          <Label>New value</Label>
          <Input type={field === "amount" ? "number" : field === "date" ? "date" : "text"}
            value={value} onChange={(e) => setValue(e.target.value)} data-testid="cash-correct-value" />
          <Label>Reason</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Audit note for this correction" data-testid="cash-correct-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-sky-600 hover:bg-sky-700 text-white" onClick={go} disabled={busy} data-testid="cash-correct-go">
            {busy ? "Saving…" : "Record modification"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddTxn({ accounts, defaultAcct, canPersonal, editing, duplicateOf, onClose, onSaved }) {
  const src = editing || duplicateOf;
  const [f, setF] = useState(src
    ? { account_id: src.account_id || defaultAcct || "", type: src.type || "CASH_SALE", date: duplicateOf ? todayStr() : (src.date || todayStr()), amount: src.amount ?? "", party: src.party || "", particulars: src.particulars || "", category: src.category || "", reference: src.reference || "", direction: src.direction || "IN" }
    : { account_id: defaultAcct || "", type: "CASH_SALE", date: todayStr(), amount: "", party: "", particulars: "", category: "", reference: "", direction: "IN" });
  const [busy, setBusy] = useState(false);
  const [asDraft, setAsDraft] = useState(false);
  const [sugg, setSugg] = useState({ parties: [], particulars: [], categories: [] });
  const [recentRows, setRecentRows] = useState([]);
  useEffect(() => {
    const acct = src?.account_id || defaultAcct;
    Promise.all([
      acct ? api.get("/cash-control/txns", { params: { account_id: acct, limit: 300 } }).then((r) => r.data.items || []).catch(() => []) : Promise.resolve([]),
      api.get("/cash-control/payees").then((r) => r.data.payees || []).catch(() => []),
      api.get("/cash-control/categories").then((r) => r.data.categories || CASH_CATEGORIES).catch(() => CASH_CATEGORIES),
    ]).then(([rows, payees, cats]) => {
      const uniq = (k) => [...new Set(rows.map((r) => (r[k] || "").trim()).filter(Boolean))].slice(0, 60);
      setRecentRows(rows);
      setSugg({
        parties: [...new Set([...payees, ...uniq("party")])],
        particulars: uniq("particulars"),
        categories: [...new Set([...cats, ...uniq("category")])],
      });
    });
  }, [src, defaultAcct]);
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const onType = (v) => {
    setF((s) => {
      const next = { ...s, type: v };
      // Remember Last: prefill Category / Paid-To from the most recent voucher of this type.
      if (!editing) {
        const last = recentRows.find((r) => r.type === v && (r.category || r.party));
        if (last) {
          if (!s.category && last.category) next.category = last.category;
          if (!s.party && last.party) next.party = last.party;
        }
      }
      return next;
    });
  };
  const save = async (again) => {
    if (!f.account_id || !f.amount) { toast.error("Account and amount are required"); return; }
    setBusy(true);
    try {
      if (editing) {
        // Only send fields the server actually patches — never promise more.
        await api.patch(`/cash-control/txns/${editing.id}`, {
          type: f.type, direction: f.type === "ADJUSTMENT" ? f.direction : undefined,
          date: f.date, amount: Number(f.amount), party: f.party,
          particulars: f.particulars, category: f.category, reference: f.reference });
        toast.success("Cash transaction updated");
        onSaved(); onClose();
      } else {
        await api.post("/cash-control/txns", { ...f, amount: Number(f.amount), status: asDraft ? "DRAFT" : undefined });
        toast.success(asDraft ? "Saved as draft" : "Cash transaction saved");
        onSaved();
        if (again) setF((s) => ({ ...s, amount: "", party: "", particulars: "", reference: "" }));
        else onClose();
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "object" ? (d.message || d.recommended_action) : d) || "Save failed");
    }
    finally { setBusy(false); }
  };
  const types = TXN_TYPES.filter((t) => canPersonal || !t.startsWith("PERSONAL"));
  return (
    <Dialog open onOpenChange={(o) => { if (!o) setTimeout(onClose, 150); }}>
      <DialogContent data-testid="cash-add-txn-dialog">
        <DialogHeader><DialogTitle>{editing ? `Edit cash transaction ${editing.number || ""}` : duplicateOf ? "Duplicate cash transaction" : "Add cash transaction"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Cash account</Label>
            <select disabled={!!editing} className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white disabled:opacity-60 disabled:bg-slate-50" value={f.account_id} onChange={(e) => u("account_id", e.target.value)} data-testid="cash-txn-account">
              <option value="">Select…</option>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select></div>
          <div><Label>Type</Label>
            <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[12px] bg-white disabled:opacity-60 disabled:bg-slate-50" value={f.type} onChange={(e) => onType(e.target.value)} data-testid="cash-txn-type">
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select></div>
          <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => u("date", e.target.value)} data-testid="cash-txn-date" /></div>
          <div><Label>Amount</Label><Input type="number" min={0} value={f.amount} onChange={(e) => u("amount", e.target.value)} data-testid="cash-txn-amount" /></div>
          {f.type === "ADJUSTMENT" && (
            <div><Label>Direction</Label>
              <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white" value={f.direction} onChange={(e) => u("direction", e.target.value)}>
                <option value="IN">IN (increase)</option><option value="OUT">OUT (decrease)</option>
              </select></div>
          )}
          <div><Label>Received from / Paid to</Label>
            <Input list="cash-party-list" value={f.party} onChange={(e) => u("party", e.target.value)} data-testid="cash-txn-party" />
            <datalist id="cash-party-list">{sugg.parties.map((p) => <option key={p} value={p} />)}</datalist></div>
          <div className="col-span-2"><Label>Particulars</Label>
            <Input list="cash-particulars-list" value={f.particulars} onChange={(e) => u("particulars", e.target.value)} data-testid="cash-txn-particulars" />
            <datalist id="cash-particulars-list">{sugg.particulars.map((p) => <option key={p} value={p} />)}</datalist></div>
          <div><Label>Category</Label>
            <Input list="cash-category-list" value={f.category} onChange={(e) => u("category", e.target.value)} data-testid="cash-txn-category" />
            <datalist id="cash-category-list">{sugg.categories.map((c) => <option key={c} value={c} />)}</datalist></div>
          <div><Label>Reference</Label><Input value={f.reference} onChange={(e) => u("reference", e.target.value)} data-testid="cash-txn-reference" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {!editing && (
            <label className="flex items-center gap-1.5 text-[12px] text-slate-600 mr-auto" data-testid="cash-txn-draft-toggle">
              <input type="checkbox" checked={asDraft} onChange={(e) => setAsDraft(e.target.checked)} /> Save as draft
            </label>)}
          {!editing && <Button variant="outline" onClick={() => save(true)} disabled={busy} data-testid="cash-txn-save-next">Save & add next</Button>}
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => save(false)} disabled={busy} data-testid="cash-txn-save">{busy ? "Saving…" : (editing ? "Update" : "Save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddAccount({ canPersonal, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", type: "MAIN", opening_balance: 0, opening_date: todayStr(), custodian: "" });
  const [busy, setBusy] = useState(false);
  const types = ["MAIN", "PETTY", "SERVICE", "CASH_SALES", "BUSINESS", ...(canPersonal ? ["PERSONAL"] : [])];
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try { await api.post("/cash-control/accounts", { ...f, opening_balance: Number(f.opening_balance) }); toast.success("Cash account created"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) setTimeout(onClose, 150); }}>
      <DialogContent data-testid="cash-add-account-dialog">
        <DialogHeader><DialogTitle>New cash account</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} placeholder="SUNTRONIX - MAIN CASH" data-testid="cash-account-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white" value={f.type} onChange={(e) => setF((s) => ({ ...s, type: e.target.value }))} data-testid="cash-account-type">
                {types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><Label>Opening balance</Label><Input type="number" value={f.opening_balance} onChange={(e) => setF((s) => ({ ...s, opening_balance: e.target.value }))} data-testid="cash-account-opening" /></div>
            <div><Label>Opening date</Label><Input type="date" value={f.opening_date} onChange={(e) => setF((s) => ({ ...s, opening_date: e.target.value }))} /></div>
            <div><Label>Custodian</Label><Input value={f.custodian} onChange={(e) => setF((s) => ({ ...s, custodian: e.target.value }))} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={save} disabled={busy} data-testid="cash-account-save">{busy ? "Saving…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const DENOMS = [2000, 500, 200, 100, 50, 20, 10, 5, 2, 1];

function DailyClosing({ acctId, accounts }) {
  const [date, setDate] = useState(todayStr());
  const [summary, setSummary] = useState(null);
  const [counts, setCounts] = useState({});
  const [phys, setPhys] = useState("");
  const [closings, setClosings] = useState([]);
  const [canReopen, setCanReopen] = useState(false);
  const [pending, setPending] = useState(null); // {type:'certify'|'reopen', closing}
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const denomTotal = DENOMS.reduce((s, d) => s + d * (Number(counts[d]) || 0), 0);
  const physical = phys !== "" ? Number(phys) : (denomTotal || "");
  const load = useCallback(async () => {
    if (!acctId) return;
    try {
      const { data } = await api.get("/cash-control/closing/summary", { params: { account_id: acctId, date } });
      setSummary(data);
      const cl = await api.get("/cash-control/closing", { params: { account_id: acctId } });
      setClosings(cl.data.items || []); setCanReopen(!!cl.data.can_reopen);
    } catch { /* */ }
  }, [acctId, date]);
  useEffect(() => { load(); }, [load]);
  const closeDay = async () => {
    try {
      const denominations = Object.fromEntries(DENOMS.filter((d) => Number(counts[d]) > 0).map((d) => [String(d), Number(counts[d])]));
      const body = { account_id: acctId, date, denominations: Object.keys(denominations).length ? denominations : null };
      if (phys !== "") body.physical_amount = Number(phys);
      const { data } = await api.post("/cash-control/closing", body);
      toast.success(`Day closed — difference ${money(data.difference)}. Awaiting certification.`);
      setCounts({}); setPhys(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const runPending = async () => {
    if (!pending) return;
    if (pending.type === "reopen" && !reason.trim()) { toast.error("A reason is required to reopen"); return; }
    setBusy(true);
    try {
      if (pending.type === "certify") {
        await api.post(`/cash-control/closing/${pending.closing.id}/certify`);
        toast.success("Day certified & locked");
      } else {
        const { data } = await api.post(`/cash-control/closing/${pending.closing.id}/reopen`, { reason });
        toast.success(`Day reopened — ${data.unlocked_txns} entr(ies) unlocked`);
      }
      setPending(null); setReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const name = accounts.find((a) => a.id === acctId)?.name;
  const expected = summary?.expected_closing || 0;
  const diff = physical !== "" ? Number(physical) - expected : null;
  return (
    <div data-testid="cash-daily-closing" className="grid md:grid-cols-2 gap-6">
      <div className="border border-slate-200 rounded-lg p-4">
        <div className="flex items-end gap-3 mb-3">
          <div><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="cash-closing-date" /></div>
          <div className="text-[12px] text-slate-500">{name}</div>
        </div>
        {summary && (
          <div className="space-y-1 text-[13px]">
            {[["Opening", summary.opening], ["Receipts", summary.receipts], ["Payments", summary.payments], ["Bank deposits", summary.bank_deposits], ["Adjustments", summary.adjustments]].map(([l, v]) => (
              <div key={l} className="flex justify-between"><span className="text-slate-500">{l}</span><span>{money(v)}</span></div>
            ))}
            <div className="flex justify-between font-semibold border-t pt-1"><span>Expected closing</span><span data-testid="cash-closing-expected">{money(expected)}</span></div>
            <div className="pt-2">
              <Label className="text-[12px]">Denomination count</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-1">
                {DENOMS.map((d) => (
                  <div key={d} className="flex items-center gap-1.5">
                    <span className="w-12 text-right text-[11px] text-slate-500">₹{d}</span>
                    <span className="text-slate-400 text-[11px]">×</span>
                    <Input type="number" min="0" value={counts[d] || ""} onChange={(e) => setCounts((c) => ({ ...c, [d]: e.target.value }))}
                      className="h-7 text-[12px]" data-testid={`cash-denom-${d}`} />
                    <span className="w-16 text-right text-[10px] text-slate-500">{money(d * (Number(counts[d]) || 0))}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-[12px] mt-1"><span className="text-slate-500">Counted (denominations)</span><span data-testid="cash-denom-total">{money(denomTotal)}</span></div>
            </div>
            <div className="pt-1"><Label className="text-[12px]">Physical override (optional)</Label><Input type="number" value={phys} onChange={(e) => setPhys(e.target.value)} placeholder={denomTotal ? String(denomTotal) : "counted cash"} data-testid="cash-closing-physical" /></div>
            {diff !== null && <div className="flex justify-between text-[13px]"><span className="text-slate-500">Difference</span><span className={diff !== 0 ? "text-rose-600 font-semibold" : "text-emerald-600"} data-testid="cash-closing-diff">{money(diff)}</span></div>}
            <Button className="mt-2 w-full bg-emerald-700 hover:bg-emerald-800 text-white" onClick={closeDay} data-testid="cash-closing-close">Close day (maker)</Button>
          </div>
        )}
      </div>
      <div>
        <div className="font-semibold text-sm mb-2">Closings & certification <span className="text-[11px] text-slate-400 font-normal">· {name}</span></div>
        <div className="space-y-1">
          {closings.map((c, i) => (
            <div key={c.id} className="flex items-center gap-2 text-[12px] border border-slate-200 rounded px-3 py-1.5" data-testid={`cash-closing-row-${i}`}>
              <span className="font-mono text-[10px]">{c.code}</span><span>{c.date}</span>
              <span className={`${c.difference ? "text-rose-600" : "text-emerald-600"}`}>Δ {money(c.difference)}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.status === "CERTIFIED" ? "bg-emerald-100 text-emerald-700" : c.status === "REOPENED" ? "bg-amber-100 text-amber-700" : "bg-slate-100"}`}>{c.status}</span>
              <div className="ml-auto flex gap-1">
                {c.status !== "CERTIFIED" && <button className="text-emerald-700 text-[10px] font-medium" onClick={() => { setPending({ type: "certify", closing: c }); setReason(""); }} data-testid={`cash-certify-${i}`}>Certify</button>}
                {c.status === "CERTIFIED" && canReopen && <button className="text-amber-700 text-[10px] font-medium" onClick={() => { setPending({ type: "reopen", closing: c }); setReason(""); }} data-testid={`cash-reopen-${i}`}>Reopen</button>}
              </div>
            </div>
          ))}
          {!closings.length && <div className="text-sm text-slate-400 py-4 text-center">No closings yet for this account.</div>}
        </div>
      </div>

      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent data-testid="cash-closing-confirm-dialog">
          <DialogHeader><DialogTitle>{pending?.type === "certify" ? "Certify & lock this day?" : "Reopen certified day?"}</DialogTitle></DialogHeader>
          {pending && (
            <div className="text-[13px] space-y-2">
              <div className="text-slate-600">{name} · {pending.closing.date} · {pending.closing.code}</div>
              {pending.type === "certify" ? (
                <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 text-[12px]">Certifying locks every posting for this day. It can only be undone by a <b>different</b> super-admin / management user (maker-checker).</p>
              ) : (
                <>
                  <p className="text-[12px] text-slate-500">A different checker (not whoever certified it) must reopen. Certified postings will be unlocked for correction.</p>
                  <div><Label className="text-[12px]">Reason (required)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why reopen this day?" data-testid="cash-reopen-reason" /></div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)}>Cancel</Button>
            <Button className={pending?.type === "certify" ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "bg-amber-600 hover:bg-amber-700 text-white"}
              onClick={runPending} disabled={busy} data-testid="cash-closing-confirm-btn">
              {busy ? "Working…" : (pending?.type === "certify" ? "Certify & lock" : "Reopen day")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ImportPanel({ acctId, accounts }) {
  const [batch, setBatch] = useState(null);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!acctId) { toast.error("Pick a cash account tile first"); return; }
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/cash-control/import/upload?account_id=${acctId}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setBatch(data); setPreview(null); toast.success("File read — map columns"); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
  };
  const doPreview = async () => {
    try { await api.post(`/cash-control/import/${batch.id}/mapping`, { mapping }); const { data } = await api.get(`/cash-control/import/${batch.id}/preview`); setPreview(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const commit = async () => {
    try { const { data } = await api.post(`/cash-control/import/${batch.id}/commit`); toast.success(`Imported ${data.imported} transactions`); setBatch(null); setPreview(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const name = accounts.find((a) => a.id === acctId)?.name;
  return (
    <div data-testid="cash-import" className="space-y-4">
      <div className="border border-slate-200 rounded-lg p-4">
        <div className="font-semibold text-sm mb-1">Import a Google-Sheet / Excel cash register</div>
        <p className="text-[12px] text-slate-500 mb-2">Into account: <b>{name || "— pick a tile above —"}</b>. Original file is kept as evidence; nothing posts without your review.</p>
        <input type="file" accept=".csv,.xlsx,.xls" onChange={upload} data-testid="cash-import-file" className="text-[12px]" />
      </div>
      {batch && (
        <div className="border border-slate-200 rounded-lg p-4" data-testid="cash-import-mapping">
          <div className="font-semibold text-sm mb-2">Map columns — {batch.code}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {batch.target_fields.map((tf) => (
              <div key={tf}><Label className="capitalize">{tf}</Label>
                <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[12px] bg-white" value={mapping[tf] || ""} onChange={(e) => setMapping((m) => ({ ...m, [tf]: e.target.value }))} data-testid={`cash-import-map-${tf}`}>
                  <option value="">—</option>{batch.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
            ))}
          </div>
          <Button className="mt-3" onClick={doPreview} data-testid="cash-import-preview-btn">Preview</Button>
        </div>
      )}
      {preview && (
        <div className="border border-slate-200 rounded-lg p-4" data-testid="cash-import-preview">
          <div className="flex justify-between mb-2">
            <div className="font-semibold text-sm">{preview.row_count} rows · {preview.valid} valid · {preview.duplicates} duplicates</div>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={commit} data-testid="cash-import-commit-btn">Commit import</Button>
          </div>
          <div className="overflow-x-auto max-h-72">
            <table className="w-full text-[12px]"><thead className="bg-slate-50 text-slate-500 uppercase text-[9px]"><tr>{["Date", "Type", "Party", "Amount", "Dup"].map((h) => <th key={h} className="px-2 py-1 text-left">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100">
                {preview.rows.map((r, i) => (
                  <tr key={i} className={r.duplicate ? "bg-amber-50" : ""}>
                    <td className="px-2 py-1">{r.date}</td><td className="px-2 py-1 text-[10px]">{r.type}</td>
                    <td className="px-2 py-1">{r.party}</td><td className="px-2 py-1 text-right">{money(r.amount)}</td>
                    <td className="px-2 py-1">{r.duplicate ? "DUP" : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function AuditorView({ acctId, accounts }) {
  const [date, setDate] = useState(todayStr());
  const [dateTo, setDateTo] = useState(todayStr());
  const [cat, setCat] = useState("");
  const [data, setData] = useState(null);
  const run = async (categoryOverride) => {
    if (!acctId) return;
    const c = categoryOverride !== undefined ? categoryOverride : cat;
    const params = { account_id: acctId, date };
    if (dateTo && dateTo > date) params.date_to = dateTo;
    if (c) params.category = c;
    try { const { data } = await api.get("/cash-control/auditor", { params }); setData(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { run(); }, [acctId]); // eslint-disable-line
  const isRange = !!data?.date_to;
  const totals = data?.category_totals || [];
  return (
    <div data-testid="cash-auditor">
      <div className="flex items-end gap-3 mb-3 flex-wrap">
        <div><Label>From</Label><Input type="date" value={date} max={dateTo || undefined} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="cash-auditor-date" /></div>
        <div><Label>To</Label><Input type="date" value={dateTo} min={date} onChange={(e) => setDateTo(e.target.value)} className="w-44" data-testid="cash-auditor-date-to" /></div>
        <div><Label>Category</Label>
          <select className="w-52 h-9 border border-slate-200 rounded-md px-2 text-[12px] bg-white" value={cat} onChange={(e) => { setCat(e.target.value); run(e.target.value); }} data-testid="cash-auditor-category">
            <option value="">All categories</option>
            {totals.map((t) => <option key={t.category} value={t.category}>{t.category}</option>)}
          </select>
        </div>
        <Button onClick={() => run()} data-testid="cash-auditor-run">View</Button>
        <div className="text-[12px] text-slate-500">{accounts.find((a) => a.id === acctId)?.name}{isRange ? ` · ${data.date} → ${data.date_to}` : ""}</div>
      </div>
      {totals.length > 0 && (
        <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden" data-testid="cash-auditor-cat-totals">
          <div className="bg-slate-50 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-400">Per-category totals</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 p-3 text-[12px]">
            {totals.map((t) => (
              <button key={t.category} onClick={() => { setCat(t.category); run(t.category); }} className={`flex items-center justify-between hover:bg-slate-50 rounded px-1 ${cat === t.category ? "font-semibold text-slate-900" : "text-slate-600"}`} data-testid={`cash-cat-total-${t.category}`}>
                <span className="truncate">{t.category} <span className="text-slate-400">({t.count})</span></span>
                <span className={t.net >= 0 ? "text-emerald-700" : "text-rose-700"}>{money(t.net)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {data && (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-2">
            {[["Opening", data.summary.opening], ["Receipts", data.summary.receipts], ["Payments", data.summary.payments], ["Expected closing", data.summary.expected_closing]].map(([l, v]) => (
              <div key={l} className="rounded-md border border-slate-200 bg-white px-3 py-2"><div className="text-[9px] uppercase tracking-wider text-slate-400">{l}</div><div className="text-lg font-semibold">{money(v)}</div></div>
            ))}
          </div>
          <div className="border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-[12px]"><thead className="bg-slate-50 text-slate-500 uppercase text-[9px]"><tr>{[...(isRange ? ["Date"] : []), "Voucher", "Type", "Particulars", "In", "Out", "By", "Status"].map((h) => <th key={h} className="px-2 py-2 text-left">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100">
                {data.transactions.map((t, i) => (
                  <tr key={t.id} data-testid={`cash-auditor-row-${i}`}>
                    {isRange && <td className="px-2 py-1.5 whitespace-nowrap text-[10px]">{t.date}</td>}
                    <td className="px-2 py-1.5 font-mono text-[10px]">{t.number}</td><td className="px-2 py-1.5 text-[10px]">{t.type}</td>
                    <td className="px-2 py-1.5">{t.particulars || t.party}</td>
                    <td className="px-2 py-1.5 text-right text-emerald-700">{t.direction === "IN" ? money(t.amount) : ""}</td>
                    <td className="px-2 py-1.5 text-right text-rose-700">{t.direction === "OUT" ? money(t.amount) : ""}</td>
                    <td className="px-2 py-1.5 text-[10px]">{t.created_by}</td><td className="px-2 py-1.5 text-[10px]">{t.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.closing && <div className="text-[12px] text-slate-600">Certified: {data.closing.status} · Physical {money(data.closing.physical_amount)} · Difference {money(data.closing.difference)} · by {data.closing.certified_by || "—"}</div>}
        </div>
      )}
    </div>
  );
}


// ── Controlled delete for a POSTED transaction ──────────────────────────────
function PostedDeleteDialog({ txn, onClose, onReverse, onVoid }) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) setTimeout(onClose, 150); }}>
      <DialogContent data-testid="cash-posted-delete-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Delete posted transaction</DialogTitle></DialogHeader>
        <div className="text-[13px] text-slate-600 space-y-2">
          <p>This transaction is <b>already posted</b> and affects the cash balance, so it can’t simply be erased. Choose how to remove its effect — the original is always kept for audit.</p>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px] space-y-0.5">
            <div><span className="text-slate-400">Date</span> · {txn.date}</div>
            <div><span className="text-slate-400">Particulars</span> · {txn.particulars || txn.party || "—"}</div>
            <div><span className="text-slate-400">Voucher</span> · {txn.number}</div>
            <div><span className="text-slate-400">{txn.direction === "IN" ? "Receipt" : "Payment"}</span> · {money(txn.amount)}</div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={onReverse} data-testid="cash-posted-delete-reverse"><RotateCcw className="h-4 w-4 mr-1" /> Reverse</Button>
          <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={onVoid} data-testid="cash-posted-delete-void"><Ban className="h-4 w-4 mr-1" /> Void</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Transaction history ─────────────────────────────────────────────────────
function TxnHistoryDialog({ txn, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/cash-control/txns/${txn.id}/history`).then(({ data }) => setData(data)).catch(() => setData({ events: [] }));
  }, [txn.id]);
  const badge = { CREATED: "bg-emerald-100 text-emerald-800", EDITED: "bg-slate-100 text-slate-700",
    MODIFIED: "bg-sky-100 text-sky-800", REVERSED: "bg-amber-100 text-amber-800", VOIDED: "bg-rose-100 text-rose-800" };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) setTimeout(onClose, 150); }}>
      <DialogContent data-testid="cash-history-dialog">
        <DialogHeader><DialogTitle>History · {txn.number}</DialogTitle></DialogHeader>
        {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {(data.events || []).length === 0 && <div className="text-sm text-slate-400">No history events.</div>}
            {(data.events || []).map((e, i) => (
              <div key={i} className="border border-slate-200 rounded-md p-2.5 text-[12px]" data-testid={`cash-history-event-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${badge[e.event] || "bg-slate-100"}`}>{e.event}</span>
                  <span className="text-slate-500">{e.by || "—"}</span>
                  <span className="text-slate-400 ml-auto">{(e.at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                {e.reason && <div className="text-slate-600">Reason: {e.reason}</div>}
                {e.old && <div className="text-rose-600">Old: {JSON.stringify(e.old)}</div>}
                {e.new && <div className="text-emerald-700">New: {JSON.stringify(e.new)}</div>}
                {e.detail && <div className="text-slate-500">{JSON.stringify(e.detail)}</div>}
              </div>
            ))}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit cash account ───────────────────────────────────────────────────────
function EditAccount({ account, onClose, onSaved }) {
  const [f, setF] = useState({
    name: account.name || "", type: account.type || "MAIN",
    establishment_id: account.establishment_id || "", currency: account.currency || "INR",
    custodian: account.custodian || "", location: account.location || "",
    opening_date: account.opening_date || "", notes: account.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const hasTxns = (account.txn_count || 0) > 0;
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/cash-control/accounts/${account.id}`, f); toast.success("Cash account updated"); onSaved(); }
    catch (e) { const d = e.response?.data?.detail; toast.error((typeof d === "object" ? d.message : d) || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) setTimeout(onClose, 150); }}>
      <DialogContent data-testid="cash-edit-account-dialog">
        <DialogHeader><DialogTitle>Edit cash account</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name</Label><Input value={f.name} onChange={(e) => u("name", e.target.value)} data-testid="cash-edit-account-name" /></div>
          <div><Label>Type</Label>
            <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white" value={f.type} onChange={(e) => u("type", e.target.value)} data-testid="cash-edit-account-type">
              {["MAIN", "PETTY", "SERVICE", "CASH_SALES", "BUSINESS", "PERSONAL", "ESTABLISHMENT"].map((t) => <option key={t}>{t}</option>)}
            </select></div>
          <div><Label>Establishment</Label><Input value={f.establishment_id} onChange={(e) => u("establishment_id", e.target.value)} /></div>
          <div><Label>Currency</Label><Input value={f.currency} onChange={(e) => u("currency", e.target.value)} /></div>
          <div><Label>Custodian</Label><Input value={f.custodian} onChange={(e) => u("custodian", e.target.value)} data-testid="cash-edit-account-custodian" /></div>
          <div><Label>Location</Label><Input value={f.location} onChange={(e) => u("location", e.target.value)} /></div>
          <div><Label>Opening date</Label><Input type="date" value={f.opening_date} onChange={(e) => u("opening_date", e.target.value)} /></div>
          <div className="col-span-2"><Label>Notes</Label><Input value={f.notes} onChange={(e) => u("notes", e.target.value)} /></div>
        </div>
        {hasTxns && <p className="text-[11px] text-amber-700 mt-1">Opening balance is locked because this account already has {account.txn_count} transaction(s). Post an ADJUSTMENT entry instead.</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={save} disabled={busy} data-testid="cash-edit-account-save">{busy ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Account delete blocked (has history) ────────────────────────────────────
function AccountDeleteBlockedDialog({ account, onClose, onDeactivate, onView, onForceDelete, canForce }) {
  const [confirmText, setConfirmText] = useState("");
  const armed = confirmText.trim().toUpperCase() === "DELETE";
  return (
    <Dialog open onOpenChange={(o) => { if (!o) setTimeout(onClose, 150); }}>
      <DialogContent data-testid="cash-account-delete-blocked-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> This cash account has financial history</DialogTitle></DialogHeader>
        <p className="text-[13px] text-slate-600">
          <b>{account.name}</b> has {account.txn_count} transaction(s). The safe option is to <b>deactivate</b> it (kept for audit). {canForce && "As a super admin you may also delete it permanently."}
        </p>
        {canForce && (
          <div className="mt-1 border border-rose-200 bg-rose-50 rounded-lg p-3" data-testid="cash-account-force-block">
            <div className="text-[12px] font-semibold text-rose-700 flex items-center gap-1.5"><Trash2 className="h-3.5 w-3.5" /> Permanent delete</div>
            <p className="text-[11px] text-rose-700/90 mt-1">
              This removes the account <b>and all {account.txn_count} transaction(s)</b>. This cannot be undone.
              Type <b>DELETE</b> to confirm.
            </p>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE" className="mt-2 h-8" data-testid="cash-account-force-confirm" />
          </div>)}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="outline" onClick={onView} data-testid="cash-account-blocked-view">View transactions</Button>
          <Button className="bg-slate-800 hover:bg-slate-900 text-white" onClick={onDeactivate} data-testid="cash-account-blocked-deactivate"><Power className="h-4 w-4 mr-1" /> Deactivate</Button>
          {canForce && (
            <Button className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40"
              disabled={!armed} onClick={onForceDelete} data-testid="cash-account-force-delete">
              <Trash2 className="h-4 w-4 mr-1" /> Delete permanently</Button>)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Cash account history (audit trail) ──────────────────────────────────────
function AccountHistoryDialog({ account, onClose }) {
  const edits = account.edits || [];
  return (
    <Dialog open onOpenChange={(o) => { if (!o) setTimeout(onClose, 150); }}>
      <DialogContent data-testid="cash-account-history-dialog">
        <DialogHeader><DialogTitle>History · {account.name}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto text-[12px]">
          <div className="border border-slate-200 rounded-md p-2.5">
            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">CREATED</span>
            <span className="text-slate-500 ml-2">{account.created_by || "—"}</span>
            <span className="text-slate-400 ml-2">{(account.created_at || "").slice(0, 16).replace("T", " ")}</span>
          </div>
          {edits.map((e, i) => (
            <div key={i} className="border border-slate-200 rounded-md p-2.5" data-testid={`cash-account-history-${i}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">EDITED</span>
                <span className="text-slate-500">{e.by}</span>
                <span className="text-slate-400 ml-auto">{(e.at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
              <div className="text-rose-600">Old: {JSON.stringify(e.old)}</div>
              <div className="text-emerald-700">New: {JSON.stringify(e.new)}</div>
            </div>
          ))}
          {!edits.length && <div className="text-slate-400">No edits recorded yet.</div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
